import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MarginAlert } from '@/types';
import { apiService } from '@/services/apiService';
import LCIcon from '@/components/common/LCIcon';
import { fmtCurrency, LCBtn, LCChip, CLS, STATUS_LABEL, STATUS_FILL } from '@/components/common/LCPrimitives';

function timeAgo(ts: string): string {
  const m = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function fmtPctVal(v: number | null): string {
  if (v == null) return '—';
  return `${(v * 100).toFixed(2)}%`;
}

function normalizeStatus(s: string | null | undefined): string {
  if (!s) return '';
  return s.toUpperCase().replace(/ /g, '_');
}

const SEVERITY_ORDER = ['HOUSE_CALL', 'CRITICAL', 'RED_ALERT', 'AMBER_ALERT', 'GREEN'];

const AlertsView: React.FC = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<MarginAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acknowledgingAlerts, setAcknowledgingAlerts] = useState<Set<string>>(new Set());
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const data = await apiService.getAlerts();
      setAlerts(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch alerts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (portfolioId: string) => {
    setAcknowledgingAlerts(prev => new Set(prev).add(portfolioId));
    try {
      await apiService.acknowledgeAlert(portfolioId);
      setSuccessMessage(`Alert acknowledged and email sent for ${portfolioId}`);
      setTimeout(() => setSuccessMessage(null), 5000);
      await fetchAlerts();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
      setError(`Failed to acknowledge alert for ${portfolioId}: ${msg}`);
    } finally {
      setAcknowledgingAlerts(prev => { const n = new Set(prev); n.delete(portfolioId); return n; });
    }
  };

  const counts = alerts.reduce((acc, a) => {
    const s = normalizeStatus(a.current_status);
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const sortedAlerts = [...alerts].sort((a, b) => {
    const ai = SEVERITY_ORDER.indexOf(normalizeStatus(a.current_status));
    const bi = SEVERITY_ORDER.indexOf(normalizeStatus(b.current_status));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  if (loading) {
    return (
      <div className={`${CLS.page} flex items-center justify-center min-h-[400px]`}>
        <span className={CLS.muted}>Loading alerts…</span>
      </div>
    );
  }

  return (
    <div className={CLS.page}>
      <div className={CLS.pageHead}>
        <div>
          <h1 className={CLS.pageH1}>Margin Alerts</h1>
          <div className={CLS.pageSub}>Active margin escalations — portfolios actively margin trading with cushion (incl. cash) below thresholds.</div>
        </div>
        <div className={CLS.metaRow}>
          <div className={CLS.meta}><LCIcon name="refresh" size={14} /> Refreshing every 30s</div>
        </div>
      </div>

      {error && (
        <div className="bg-[rgba(229,0,37,0.06)] border border-[#E50025] px-4 py-3 mb-4 flex justify-between items-center">
          <span style={{ fontSize: 13 }}>{error}</span>
          <button className={CLS.iconBtn} onClick={() => setError(null)}><LCIcon name="close" size={14} /></button>
        </div>
      )}
      {successMessage && (
        <div className="bg-[rgba(31,138,74,0.08)] border border-[#1f8a4a] px-4 py-3 mb-4 flex justify-between items-center">
          <span style={{ fontSize: 13, color: '#1f8a4a' }}>{successMessage}</span>
          <button className={CLS.iconBtn} onClick={() => setSuccessMessage(null)}><LCIcon name="close" size={14} /></button>
        </div>
      )}

      {/* Counters */}
      <div className="grid grid-cols-5 gap-3 mb-[18px] max-[768px]:grid-cols-3 max-[480px]:grid-cols-2">
        {SEVERITY_ORDER.map(s => (
          <div key={s} className="bg-white border border-black/[0.06] px-4 py-3.5 flex items-center gap-3">
            <div className="w-1.5 self-stretch" style={{ background: STATUS_FILL[s] }} />
            <div>
              <div className="font-serif text-[30px] font-normal leading-none">{counts[s] || 0}</div>
              <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-[#7A7A76]">{STATUS_LABEL[s]}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className={CLS.tableWrap}>
        <table className={CLS.table}>
          <thead>
            <tr>
              <th className={CLS.th}>Created</th>
              <th className={CLS.th}>Portfolio ID</th>
              <th className={CLS.th}>Client</th>
              <th className={CLS.th}>Status</th>
              <th className={`${CLS.th} ${CLS.tdNum}`}>Cushion (w/ Cash)</th>
              <th className={`${CLS.th} ${CLS.tdNum}`}>Initial Cushion</th>
              <th className={`${CLS.th} ${CLS.tdNum}`}>Change</th>
              <th className={`${CLS.th} ${CLS.tdNum}`}>LC Req.</th>
              <th className={`${CLS.th} ${CLS.tdNum}`}>Total AUM</th>
              <th className={CLS.th}>RM</th>
              <th className={CLS.th} style={{ textAlign: 'right', width: 160 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedAlerts.map(a => {
              const change = a.surplus_cushion_with_cash_change;
              return (
                <tr key={a.id} className="transition-colors hover:bg-[#FAF9F8]">
                  <td className={`${CLS.td} ${CLS.muted}`} style={{ whiteSpace: 'nowrap' }}>{timeAgo(a.alert_created_at)}</td>
                  <td className={`${CLS.td} ${CLS.tdMono}`}>{a.portfolio_id_lig}</td>
                  <td className={`${CLS.td} ${CLS.tdClient}`}>{a.client_name || '—'}</td>
                  <td className={CLS.td}><LCChip status={a.current_status} /></td>
                  <td className={`${CLS.td} ${CLS.tdNum}`} style={{ fontWeight: 600 }}>{fmtPctVal(a.surplus_cushion_with_cash)}</td>
                  <td className={`${CLS.td} ${CLS.tdNum} ${CLS.muted}`}>{fmtPctVal(a.initial_surplus_cushion_with_cash)}</td>
                  <td className={`${CLS.td} ${CLS.tdNum}`} style={{ color: change != null ? (change < 0 ? '#E50025' : '#1f8a4a') : undefined }}>
                    {change != null ? `${change < 0 ? '▼' : '▲'} ${Math.abs(change * 100).toFixed(2)}%` : '—'}
                  </td>
                  <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtCurrency(a.lc_margin_requirement)}</td>
                  <td className={`${CLS.td} ${CLS.tdNum}`}>{fmtCurrency(a.total_aum)}</td>
                  <td className={CLS.td}>{a.rm_name || '—'}</td>
                  <td className={CLS.td} style={{ textAlign: 'right' }}>
                    <LCBtn variant="ghost" size="sm" onClick={() => navigate(`/portfolio/${a.portfolio_id_lig}`)}>
                      View <LCIcon name="arrow" size={12} />
                    </LCBtn>
                    {a.is_acknowledged ? (
                      <span className={`${CLS.chip} bg-transparent border border-black/[0.18] text-[#1f8a4a] border-[rgba(31,138,74,0.32)] ml-2`}>
                        <LCIcon name="check" size={11} /> Acknowledged
                      </span>
                    ) : (
                      <LCBtn variant="primary" size="sm" className="ml-2" onClick={() => handleAcknowledge(a.portfolio_id_lig)} disabled={acknowledgingAlerts.has(a.portfolio_id_lig)}>
                        <LCIcon name="mail" size={12} />
                        {acknowledgingAlerts.has(a.portfolio_id_lig) ? '…' : 'Acknowledge'}
                      </LCBtn>
                    )}
                  </td>
                </tr>
              );
            })}
            {sortedAlerts.length === 0 && (
              <tr><td colSpan={11} className={`${CLS.td} ${CLS.muted}`} style={{ padding: 30, textAlign: 'center' }}>No active alerts at this time.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className={`mt-6 text-[12px] ${CLS.muted} leading-[1.7]`}>
        <div className="font-bold mb-1 uppercase tracking-[0.1em] text-[10px]">Column Notes</div>
        <div><strong>Cushion (w/ Cash):</strong> Latest surplus cushion including cash &amp; cash equivalents — used for alert triggering.</div>
        <div><strong>Initial Cushion:</strong> Cushion when current status level was entered (resets on status change).</div>
        <div><strong>Change:</strong> Delta since entering current status level.</div>
        <div className="mt-1.5">Alerts are removed when portfolios return to Green or stop margin trading.</div>
      </div>
    </div>
  );
};

export default AlertsView;
